import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Grid,
  Image,
  List,
  Paper,
  Skeleton,
  Spoiler,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  TypographyStylesProvider,
} from '@mantine/core'
import type { LoaderFunctionArgs } from '@remix-run/node'
import {
  Await,
  Link,
  type MetaFunction,
  data,
  useLoaderData,
} from '@remix-run/react'
import { Suspense } from 'react'
import { TbChevronDown, TbChevronUp } from 'react-icons/tb'
import { searchCompanyByEventIDQuery, searchEventByIdQuery } from '~/bootstrap'
import { Breadcrumbs } from '~/components/breadcrumbs'
import { CompanyDetail } from '~/components/company-detail'
import { JobDescription } from '~/components/job-description'
import { NotFound } from '~/components/not-found'

import { v4 as uuid } from 'uuid'
import MultiLineText from '~/components/multi-line-text'
import {
  createNumArray,
  generateMetaDescriptor,
} from '~/core/shared/utils/misc'

import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.locale('ja')

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const id = params.id || ''

  const eventPromise = searchEventByIdQuery.execute({
    id,
    limitToOpenEvents: true,
  })
  const event = await eventPromise

  const companyPromise = searchCompanyByEventIDQuery.execute({
    id,
    limitToPublishedCompanies: true,
  })

  return data({
    eventPromise,
    companyPromise,
    meta: {
      title:
        event.searchResults.at(0)?.metaTitle ??
        event.searchResults.at(0)?.title,
      description: event.searchResults.at(0)?.metaDescription,
      image: event.searchResults.at(0)?.coverUrl,
    },
  })
}

export const meta: MetaFunction<typeof loader> = (metaInfo) =>
  generateMetaDescriptor(metaInfo, {
    pageTitle: metaInfo.data?.meta.title || 'イベント詳細',
    description: metaInfo.data?.meta.description,
    image: metaInfo.data?.meta.image,
  })

const EventSkelton = () => {
  return (
    <>
      <Container fluid p={0}>
        <Skeleton height={400} w={'100%'} />
      </Container>

      <Container>
        <Box mt='xl'>
          <Grid>
            <Grid.Col
              span={{ base: 12, sm: 12, md: 9, lg: 9 }}
              order={{ base: 2, sm: 2, md: 1, lg: 1 }}
            >
              <Paper
                style={{ position: 'sticky', top: 80 }}
                shadow='xs'
                radius='md'
                p='xl'
              >
                <Skeleton height={40} maw={512} />

                <Tabs
                  value='ContentsDetail'
                  onChange={() => {}}
                  color='primary'
                  mt='xl'
                >
                  <EventTab activeTab='ContentsDetail' />

                  <Tabs.Panel value='ContentsDetail'>
                    <Box mt='lg' mb='xl'>
                      {createNumArray(4).map((num) => (
                        <Box key={num} my={'xl'}>
                          <Skeleton height={48} maw={512} />
                          <Skeleton my={'md'} height={24} w={'100%'} />
                          <Skeleton my={'md'} height={24} w={'90%'} />
                          <Skeleton my={'md'} height={24} w={'80%'} />
                          <Skeleton my={'md'} height={24} w={'100%'} />
                          <Skeleton my={'md'} height={24} w={'60%'} />
                        </Box>
                      ))}

                      <Skeleton height={40} maw={512} />

                      <Table
                        variant='vertical'
                        styles={{
                          th: { backgroundColor: 'transparent' },
                        }}
                        withTableBorder={false}
                      >
                        <Table.Tbody>
                          {createNumArray(6).map((num) => (
                            <Table.Tr key={num}>
                              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                                <Skeleton height={24} maw={64} />
                              </Table.Th>
                              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                                <Skeleton height={24} maw={64} />
                              </Table.Th>
                              <Table.Td>
                                <Skeleton my={'sm'} height={24} maw={'100%'} />
                                <Skeleton my={'sm'} height={24} w={'60%'} />
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Box>
                  </Tabs.Panel>

                  <Tabs.Panel value='CompanyInfo'>
                    <Box my='xl'>
                      <Table
                        variant='vertical'
                        styles={{
                          th: { backgroundColor: 'transparent' },
                        }}
                        withTableBorder={false}
                      >
                        <Table.Tbody>
                          {createNumArray(6).map((num) => (
                            <Table.Tr key={num}>
                              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                                <Skeleton height={24} maw={64} />
                              </Table.Th>
                              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                                <Skeleton height={24} maw={64} />
                              </Table.Th>
                              <Table.Td>
                                <Skeleton my={'sm'} height={24} maw={'100%'} />
                                <Skeleton my={'sm'} height={24} w={'60%'} />
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Box>
                  </Tabs.Panel>

                  <Tabs.Panel value='JobDescription'>
                    <Box my='xl'>
                      <Table
                        variant='vertical'
                        styles={{
                          th: { backgroundColor: 'transparent' },
                        }}
                        withTableBorder={false}
                      >
                        <Table.Tbody>
                          {createNumArray(6).map((num) => (
                            <Table.Tr key={num}>
                              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                                <Skeleton height={24} maw={64} />
                              </Table.Th>
                              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                                <Skeleton height={24} maw={64} />
                              </Table.Th>
                              <Table.Td>
                                <Skeleton my={'sm'} height={24} maw={'100%'} />
                                <Skeleton my={'sm'} height={24} w={'60%'} />
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Box>
                  </Tabs.Panel>
                </Tabs>
              </Paper>
            </Grid.Col>

            <Grid.Col
              span={{ base: 12, sm: 12, md: 3, lg: 3 }}
              order={{ base: 1, sm: 1, md: 2, lg: 2 }}
            >
              <Paper
                style={{ position: 'sticky', top: 80 }}
                shadow='xs'
                radius='md'
                p='lg'
              >
                <Center my='xs'>
                  <Skeleton height={48} w={'70%'} />
                </Center>
                <Center mb='xs'>
                  <Skeleton height={24} maw={'100%'} />
                </Center>
                <Center>
                  <Skeleton height={24} maw={'50%'} />
                </Center>

                <Spoiler
                  mt={'md'}
                  maxHeight={160}
                  showLabel={
                    <Center>
                      <Text fw='bold' size='sm' mr='xs'>
                        すべて表示
                      </Text>
                      <TbChevronDown size={16} />
                    </Center>
                  }
                  hideLabel={
                    <Center>
                      <Text fw='bold' size='sm' mr='xs'>
                        折りたたむ
                      </Text>
                      <TbChevronUp size={16} />
                    </Center>
                  }
                  transitionDuration={0}
                  styles={{
                    control: { width: '100%' },
                  }}
                >
                  <Stack gap='sm'>
                    <Skeleton height={18} w={'100%'} px={0} />
                    <Skeleton height={18} w={'60%'} px={0} />
                    <Skeleton height={18} w={'100%'} px={0} />
                    <Skeleton height={18} w={'60%'} px={0} />
                    <Skeleton height={18} w={'100%'} px={0} />
                    <Skeleton height={18} w={'60%'} px={0} />
                  </Stack>
                </Spoiler>

                <Stack my='md' />
                <Button
                  fullWidth
                  variant='filled'
                  color='primary'
                  loading
                  loaderProps={{ type: 'dots' }}
                >
                  申し込む
                </Button>
              </Paper>
            </Grid.Col>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const EventTab = ({ activeTab }: { activeTab: string }) => {
  return (
    <>
      <Tabs.List grow justify='space-between'>
        <Tabs.Tab value='ContentsDetail'>
          <Text
            fw='bold'
            c={activeTab === 'ContentsDetail' ? 'primary' : 'secondary'}
          >
            内容紹介
          </Text>
        </Tabs.Tab>

        <Tabs.Tab value='CompanyInfo'>
          <Text
            fw='bold'
            c={activeTab === 'CompanyInfo' ? 'primary' : 'secondary'}
          >
            会社情報
          </Text>
        </Tabs.Tab>

        <Tabs.Tab value='JobDescription'>
          <Text
            fw='bold'
            c={activeTab === 'JobDescription' ? 'primary' : 'secondary'}
          >
            募集要項
          </Text>
        </Tabs.Tab>
      </Tabs.List>
    </>
  )
}

export default function Event() {
  const { eventPromise, companyPromise } = useLoaderData<typeof loader>()

  return (
    <Suspense fallback={<EventSkelton />}>
      <Await resolve={eventPromise} errorElement={<div>エラー</div>}>
        {(event) => {
          const e = event?.searchResults.at(0)
          if (!e) return <NotFound />

          return (
            <>
              {/* Breadcrumbs PC */}
              <Box py='sm' px='xl' visibleFrom='sm'>
                <Breadcrumbs
                  items={[
                    {
                      id: 'events',
                      title: 'イベント一覧',
                      href: '/events',
                    },
                    {
                      id: `event-${e.id}`,
                      title: e.title,
                    },
                  ]}
                />
              </Box>

              {/* Breadcrumbs SP */}
              <Box py='sm' px='lg' hiddenFrom='sm'>
                <Breadcrumbs
                  items={[
                    {
                      id: 'events',
                      title: 'イベント一覧',
                      href: '/events',
                    },
                    {
                      id: `event-${e.id}`,
                      title: e.title,
                    },
                  ]}
                />
              </Box>

              <Container>
                <Container fluid p={0}>
                  <Image
                    src={e.coverUrl}
                    alt={`${e.title}のヘッダー画像`}
                    fit='contain'
                  />
                </Container>

                {/* MEMO: SP表示 */}
                <Title order={3} py='sm' hiddenFrom='md' mt={40}>
                  {e.title}
                </Title>

                <Box mt='xl'>
                  <Grid>
                    <Grid.Col
                      span={{ base: 12, sm: 12, md: 9, lg: 9 }}
                      order={{ base: 2, sm: 2, md: 1, lg: 1 }}
                    >
                      <Paper
                        style={{ position: 'sticky', top: 80 }}
                        shadow='xs'
                        radius='md'
                        p='xl'
                      >
                        {/* MEMO: PC表示 */}
                        <Title order={2} mt='md' mb='xl' visibleFrom='sm'>
                          {e.title}
                        </Title>

                        <Tabs
                          defaultValue='ContentsDetail'
                          color='primary'
                          mt='xl'
                        >
                          <EventTab activeTab='ContentsDetail' />

                          <Tabs.Panel value='ContentsDetail'>
                            <Box mt='lg' mb='xl'>
                              <TypographyStylesProvider>
                                <div
                                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                                  dangerouslySetInnerHTML={{
                                    __html: e.description,
                                  }}
                                />
                              </TypographyStylesProvider>

                              <Title order={3} c='primary' my='xl'>
                                当日プログラムと注意事項
                              </Title>

                              <Table
                                variant='vertical'
                                styles={{
                                  th: { backgroundColor: 'transparent' },
                                }}
                                withTableBorder={false}
                              >
                                <Table.Tbody>
                                  {e.place && (
                                    <Table.Tr>
                                      <Table.Th
                                        w={80}
                                        py='xl'
                                        px={0}
                                        hiddenFrom='sm'
                                      >
                                        <Title order={5}>場所</Title>
                                      </Table.Th>
                                      <Table.Th
                                        w={160}
                                        py='xl'
                                        px={0}
                                        visibleFrom='sm'
                                      >
                                        <Title order={5}>場所</Title>
                                      </Table.Th>
                                      <Table.Td>
                                        <MultiLineText>{e.place}</MultiLineText>
                                      </Table.Td>
                                    </Table.Tr>
                                  )}

                                  {e.program && (
                                    <Table.Tr>
                                      <Table.Th
                                        w={80}
                                        py='xl'
                                        px={0}
                                        hiddenFrom='sm'
                                      >
                                        <Title order={5}>プログラム</Title>
                                      </Table.Th>
                                      <Table.Th
                                        w={160}
                                        py='xl'
                                        px={0}
                                        visibleFrom='sm'
                                      >
                                        <Title order={5}>プログラム</Title>
                                      </Table.Th>
                                      <Table.Td>
                                        <MultiLineText>
                                          {e.program}
                                        </MultiLineText>
                                      </Table.Td>
                                    </Table.Tr>
                                  )}

                                  {e.shouldBring && (
                                    <Table.Tr>
                                      <Table.Th
                                        w={80}
                                        py='xl'
                                        px={0}
                                        hiddenFrom='sm'
                                      >
                                        <Title order={5}>持ち物</Title>
                                      </Table.Th>
                                      <Table.Th
                                        w={160}
                                        py='xl'
                                        px={0}
                                        visibleFrom='sm'
                                      >
                                        <Title order={5}>持ち物</Title>
                                      </Table.Th>
                                      <Table.Td>
                                        <MultiLineText>
                                          {e.shouldBring}
                                        </MultiLineText>
                                      </Table.Td>
                                    </Table.Tr>
                                  )}

                                  {e.dressCode && (
                                    <Table.Tr>
                                      <Table.Th
                                        w={80}
                                        py='xl'
                                        px={0}
                                        hiddenFrom='sm'
                                      >
                                        <Title order={5}>服装</Title>
                                      </Table.Th>
                                      <Table.Th
                                        w={160}
                                        py='xl'
                                        px={0}
                                        visibleFrom='sm'
                                      >
                                        <Title order={5}>服装</Title>
                                      </Table.Th>
                                      <Table.Td>
                                        <MultiLineText>
                                          {e.dressCode}
                                        </MultiLineText>
                                      </Table.Td>
                                    </Table.Tr>
                                  )}

                                  {e.emergencyContactInfo && (
                                    <Table.Tr>
                                      <Table.Th
                                        w={80}
                                        py='xl'
                                        px={0}
                                        hiddenFrom='sm'
                                      >
                                        <Title order={5}>
                                          当日の緊急連絡先
                                        </Title>
                                      </Table.Th>
                                      <Table.Th
                                        w={160}
                                        py='xl'
                                        px={0}
                                        visibleFrom='sm'
                                      >
                                        <Title order={5}>
                                          当日の緊急連絡先
                                        </Title>
                                      </Table.Th>
                                      <Table.Td>
                                        <MultiLineText>
                                          {e.emergencyContactInfo}
                                        </MultiLineText>
                                      </Table.Td>
                                    </Table.Tr>
                                  )}
                                  {e.note && (
                                    <Table.Tr>
                                      <Table.Th
                                        w={80}
                                        py='xl'
                                        px={0}
                                        hiddenFrom='sm'
                                      >
                                        <Title order={5}>備考</Title>
                                      </Table.Th>
                                      <Table.Th
                                        w={160}
                                        py='xl'
                                        px={0}
                                        visibleFrom='sm'
                                      >
                                        <Title order={5}>備考</Title>
                                      </Table.Th>
                                      <Table.Td>
                                        <MultiLineText>{e.note}</MultiLineText>
                                      </Table.Td>
                                    </Table.Tr>
                                  )}
                                </Table.Tbody>
                              </Table>
                            </Box>
                          </Tabs.Panel>

                          <Tabs.Panel value='CompanyInfo'>
                            <Box my='xl'>
                              <Await
                                resolve={companyPromise}
                                errorElement={<div>エラー</div>}
                              >
                                {(company) => {
                                  const c = company?.searchResults.at(0)
                                  if (!c) return null
                                  return <CompanyDetail data={c} />
                                }}
                              </Await>
                            </Box>
                          </Tabs.Panel>

                          <Tabs.Panel value='JobDescription'>
                            <Box my='xl'>
                              <Await
                                resolve={companyPromise}
                                errorElement={<div>エラー</div>}
                              >
                                {(company) => {
                                  const c = company?.searchResults.at(0)

                                  if (!c) return null
                                  return <JobDescription data={c} />
                                }}
                              </Await>
                            </Box>
                          </Tabs.Panel>
                        </Tabs>
                      </Paper>
                    </Grid.Col>

                    <Grid.Col
                      span={{ base: 12, sm: 12, md: 3, lg: 3 }}
                      order={{ base: 1, sm: 1, md: 2, lg: 2 }}
                    >
                      <Paper
                        style={{ position: 'sticky', top: 80 }}
                        shadow='xs'
                        radius='md'
                        p='lg'
                      >
                        <Center
                          my='xs'
                          component={Link}
                          to={`/companies/${e.companyId}`}
                        >
                          <Image
                            src={e.organizerLogoUrl}
                            style={{ width: '100px' }}
                            alt={`${e.organizerName}のロゴ画像`}
                          />
                        </Center>
                        <Center
                          mb='xs'
                          component={Link}
                          to={`/companies/${e.companyId}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <Title order={5} c='primary'>
                            {e.organizerName}
                          </Title>
                        </Center>
                        <Center>
                          <Badge
                            variant='outline'
                            size='md'
                            radius='xs'
                            color='secondary'
                            my='xs'
                          >
                            {e.category}
                          </Badge>
                        </Center>

                        {/* イベントが3つ以上ある場合に初期状態ではイベントを3つまで表示  */}
                        <Spoiler
                          maxHeight={160}
                          showLabel={
                            <Center>
                              <Text fw='bold' size='sm'>
                                すべて表示
                              </Text>
                              <TbChevronDown size={16} />
                            </Center>
                          }
                          hideLabel={
                            <Center>
                              <Text fw='bold' size='sm'>
                                折りたたむ
                              </Text>
                              <TbChevronUp size={16} />
                            </Center>
                          }
                          transitionDuration={0}
                          styles={{
                            control: { width: '100%' },
                          }}
                        >
                          <List spacing='xs' size='sm' center>
                            {e.upcomingEventSessions.map(
                              ({
                                startAt,
                                endAt,
                                place,
                                area,
                              }: {
                                startAt: Date
                                endAt: Date
                                place: string
                                area: string | null
                              }) => {
                                const eventStartAt = dayjs(startAt)
                                  .tz('Asia/Tokyo')
                                  .format('M/D HH:mm')
                                const eventEndAt = dayjs(endAt)
                                  .tz('Asia/Tokyo')
                                  .format('HH:mm')

                                return (
                                  <List.Item key={uuid()}>
                                    <Text fw='bold'>
                                      {eventStartAt}〜{eventEndAt}
                                    </Text>
                                    <Text size='sm' c='secondary'>
                                      {place === 'オンライン' ? place : area}
                                    </Text>
                                  </List.Item>
                                )
                              },
                            )}
                          </List>
                        </Spoiler>

                        <Stack my='md' />

                        {dayjs().isAfter(e.applicationEndAt) ||
                        dayjs().isBefore(e.applicationStartAt) ? (
                          <Stack gap={0}>
                            <Center my='sm'>
                              <Text size='md' fw='bold'>
                                応募受付期間
                              </Text>
                            </Center>
                            <Center>
                              <Text size='md' fw='bold'>
                                {dayjs(e.applicationStartAt)
                                  .tz('Asia/Tokyo')
                                  .format('M/D H:mm')}
                              </Text>
                            </Center>

                            <Center>
                              <Text
                                size='md'
                                fw='bold'
                                style={{
                                  transform: 'rotate(-90deg)',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                〜
                              </Text>
                            </Center>

                            {e.applicationEndAt ? (
                              <Center>
                                <Text size='md' mb='md' fw='bold'>
                                  {dayjs(e.applicationEndAt)
                                    .tz('Asia/Tokyo')
                                    .format('M/D H:mm')}
                                </Text>
                              </Center>
                            ) : (
                              ''
                            )}
                          </Stack>
                        ) : null}

                        <Button
                          fullWidth
                          variant='filled'
                          color='primary'
                          component={Link}
                          disabled={
                            dayjs().isAfter(e.applicationEndAt) ||
                            dayjs().isBefore(e.applicationStartAt)
                          }
                          to={`/events/register/${e.id}`}
                          onClick={(event) => {
                            if (
                              dayjs().isAfter(e.applicationEndAt) ||
                              dayjs().isBefore(e.applicationStartAt)
                            )
                              event.preventDefault()
                          }}
                          h={46}
                        >
                          申し込む
                        </Button>
                      </Paper>
                    </Grid.Col>
                  </Grid>
                </Box>
              </Container>
            </>
          )
        }}
      </Await>
    </Suspense>
  )
}
